import { __rest, __assign } from 'tslib';
import React, { forwardRef, useRef, useLayoutEffect } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import '@popperjs/core';
import { testIdGen } from '../../utils/testIdGen.esm.js';

/**
 * Lets users toggle a boolean value.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Checkbox } from "@panwds/react-ui";
 * ```
 */
var Checkbox = forwardRef(function (_a, incomingRef) {
    var _b;
    var _c = _a.addClassName, addClassName = _c === void 0 ? "" : _c, checked = _a.checked, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, _d = _a.disabled, disabled = _d === void 0 ? false : _d, indeterminate = _a.indeterminate, isError = _a.isError, onChange = _a.onChange, _e = _a.readOnly, readOnly = _e === void 0 ? false : _e, children = _a.children, passThrough = __rest(_a, ["addClassName", "checked", "dataMetrics", "dataTestId", "disabled", "indeterminate", "isError", "onChange", "readOnly", "children"]);
    var labelClasses = classNames("tw-flex tw-text-xs tw-font-normal tw-leading-5 tw-font-lato tw-items-center tw-space-x-2", isError && "tw-text-red-700", disabled && "tw-cursor-not-allowed", !disabled && !readOnly && "tw-cursor-pointer");
    var checkboxClasses = classNames("tw-m-0 tw-font-lato tw-leading-4 tw-font-black tw-border-solid tw-border tw-form-checkbox dark:tw-border-gray-700 dark:tw-bg-gray-1000 hover:tw-custom-ring-blue-200 tw-rounded-xs tw-text-icon-blue-600 ", isError && !disabled
        ? "tw-border-red-700 tw-text-red-700"
        : "tw-border-gray-300", !disabled && !readOnly
        ? "hover:tw-custom-ring focus:tw-ring focus:tw-ring-offset-0 focus:tw-ring-blue-200 dark:focus:tw-ring-blue-700 dark:focus:tw-border-gray-700"
        : "focus:tw-ring-0 focus:tw-ring-offset-0", disabled && "tw-bg-gray-100 tw-cursor-not-allowed dark:tw-bg-gray-600", !disabled && !readOnly && "tw-cursor-pointer", addClassName);
    var defaultRef = useRef(null);
    var useableRef = (_b = incomingRef) !== null && _b !== void 0 ? _b : defaultRef;
    var datatestid = dataTestId || children
        ? "checkbox-".concat(typeof children === "string" && testIdGen(children))
        : "checkbox";
    useLayoutEffect(function () {
        if ((useableRef === null || useableRef === void 0 ? void 0 : useableRef.current) && checked !== undefined)
            useableRef.current.checked = checked;
        if ((useableRef === null || useableRef === void 0 ? void 0 : useableRef.current) && indeterminate !== undefined)
            useableRef.current.indeterminate = indeterminate;
    }, [checked, indeterminate]);
    return (React.createElement("label", { className: labelClasses },
        React.createElement("input", __assign({ "data-metrics": dataMetrics, "data-testid": datatestid, "aria-checked": indeterminate ? "mixed" : checked, checked: checked, disabled: disabled || readOnly, onChange: onChange, ref: useableRef, readOnly: readOnly, type: "checkbox", className: checkboxClasses }, passThrough)),
        children && React.createElement("span", null, children)));
});
Checkbox.displayName = "Checkbox";

export { Checkbox };
//# sourceMappingURL=Checkbox.esm.js.map
